import { useEffect, useState } from 'react'
import {
  executiveApi,
  type ExecutiveAnomalies,
  type ExecutiveAnomaly,
  type ExecutiveBaselines,
  type ExecutiveDrill,
  type ExecutiveFilters,
} from '../api/executive'
import { Button, Callout, Card, CardHeader, CardContent, CircularProgress, DataTable, DataTableEmptyRow, List, ListItem, StatusPill } from './ui'

/**
 * F-082 (TASK-0336, REQ-273-001) — "management by exception": in cima al cockpit Direzione l'elenco delle
 * anomalie dei KPI (deviazione dalla media, z-score, trend, soglie dichiarate), ordinate per scostamento. Ogni
 * anomalia con una pratica dietro è cliccabile verso il drill-down del cockpit. Sotto, le baseline di ogni KPI.
 * NESSUN importo. "Dati insufficienti" è uno stato dichiarato: non vuol dire che non ci siano anomalie.
 */

const BASELINE_REASONS: Record<string, string> = {
  copertura: 'storico non disponibile per tutta la finestra',
  campione: 'campione troppo piccolo',
}

const it = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 2 })

function formatValue(a: { kpi: string }, v: number | null): string {
  if (v === null) return '—'
  return a.kpi === 'rejectRate'
    ? `${(v * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`
    : it(v)
}

function SeverityBadge({ severity }: { severity: ExecutiveAnomaly['severity'] }) {
  return <StatusPill tone={severity === 'alta' ? 'error' : 'warning'}>{severity}</StatusPill>
}

interface Props {
  /** Filtri GIÀ applicati al cockpit: le anomalie li rispettano. */
  filters: ExecutiveFilters
  onDrill: (d: { drill: ExecutiveDrill; title: string }) => void
}

export function KpiAnomaliesPanel({ filters, onDrill }: Props) {
  const [data, setData] = useState<ExecutiveAnomalies | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAll, setShowAll] = useState(false)

  const [showBaselines, setShowBaselines] = useState(false)
  const [baselines, setBaselines] = useState<ExecutiveBaselines | null>(null)
  const [baselinesError, setBaselinesError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setBaselines(null)
    executiveApi
      .anomalies(filters)
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) { setData(null); setError(e instanceof Error ? e.message : 'Errore') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [filters])

  useEffect(() => {
    if (!showBaselines || baselines) return
    let cancelled = false
    setBaselinesError('')
    executiveApi
      .baselines(filters, undefined, 'all')
      .then(d => { if (!cancelled) setBaselines(d) })
      .catch(e => { if (!cancelled) setBaselinesError(e instanceof Error ? e.message : 'Errore') })
    return () => { cancelled = true }
  }, [showBaselines, baselines, filters])

  const visible = data ? (showAll ? data.anomalies : data.anomalies.slice(0, 8)) : []

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title="Anomalie"
        subtitle={data ? `${data.total} segnalate - soglie provvisorie` : undefined}
      />
      {loading ? (
        <CardContent className="text-center"><CircularProgress size="sm" /></CardContent>
      ) : error ? (
        <CardContent className="md-typescale-body-small text-danger">Anomalie non disponibili. {error}</CardContent>
      ) : data ? (
        <>
          {data.dataStatus !== 'ok' && (
            <Callout tone="info" className="m-3 mb-0">
              <strong>Dati {data.dataStatus === 'insufficiente' ? 'insufficienti' : 'parzialmente insufficienti'}.</strong>{' '}
              {data.note ??
                'Alcune baseline non hanno abbastanza storico o campione.'}{' '}
              {data.insufficient} confronti su {data.evaluated} non sono calcolabili.
            </Callout>
          )}
          {data.anomalies.length === 0 ? (
            <CardContent className="md-typescale-body-small text-muted">
              {data.dataStatus === 'insufficiente'
                ? 'Nessuna anomalia segnalata perché non ci sono ancora dati sufficienti per valutarle.'
                : 'Nessuna anomalia nel periodo.'}
            </CardContent>
          ) : (
            <List aria-label="Anomalie dei KPI">
              {visible.map(a => (
                <ListItem
                  key={`${a.kind}|${a.kpi}|${a.dimension}|${a.key}|${a.baseline?.key ?? ''}`}
                  headline={
                    <>
                      <SeverityBadge severity={a.severity} /> {a.title}
                      <span className="text-muted md-typescale-body-small ms-2">{a.rules.join(', ')}</span>
                    </>
                  }
                  trailing={
                    a.drill ? (
                      <Button
                        variant="outlined"
                        size="sm"
                        className="text-nowrap"
                        onClick={() => onDrill({ drill: a.drill as ExecutiveDrill, title: a.title })}
                      >
                        Pratiche{a.numerator !== null ? ` (${a.numerator})` : ''}
                      </Button>
                    ) : (
                      <span className="md-typescale-body-small text-muted text-nowrap">nessun elenco</span>
                    )
                  }
                />
              ))}
            </List>
          )}
          <div className="d-flex justify-content-between md-typescale-body-small px-3 py-2">
            <span>
              {data.anomalies.length > 8 && (
                <Button variant="text" size="sm" className="me-3" onClick={() => setShowAll(v => !v)}>
                  {showAll ? 'Mostra meno' : `Mostra tutte (${data.anomalies.length})`}
                </Button>
              )}
            </span>
            <Button variant="text" size="sm" onClick={() => setShowBaselines(v => !v)}>
              {showBaselines ? 'Nascondi le baseline' : 'Mostra le baseline dei KPI'}
            </Button>
          </div>

          {showBaselines && (
            <div className="border-top">
              {baselinesError ? (
                <div className="p-3 md-typescale-body-small text-danger">{baselinesError}</div>
              ) : !baselines ? (
                <div className="p-3 text-center"><CircularProgress size="sm" /></div>
              ) : (
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>KPI</th>
                      <th className="text-end md-num">Periodo</th>
                      {['Media 30 gg', 'Media 90 gg', 'Anno prima', 'Periodo prec.', 'Obiettivo'].map(h => (
                        <th key={h} className="text-end md-num">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {baselines.items.map(k => (
                      <tr key={k.kpi}>
                        <td>{k.kpiLabel}</td>
                        <td className="text-end md-num">{formatValue(k, k.current)}</td>
                        {['last30', 'last90', 'samePeriodLastYear', 'previousPeriod', 'target'].map(key => {
                          const b = k.baselines.find(x => x.key === key)
                          if (!b) return <td key={key} className="text-end md-num text-muted">—</td>
                          return (
                            <td key={key} className="text-end md-num" title={b.reason ? BASELINE_REASONS[b.reason] : `${b.sample} osservazioni`}>
                              {b.sufficient ? formatValue(k, b.value) : <span className="text-muted">insufficiente</span>}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    {baselines.items.length === 0 && (
                      <DataTableEmptyRow colSpan={7} title="Nessun KPI con dati." />
                    )}
                  </tbody>
                </DataTable>
              )}
            </div>
          )}
          <ul className="md-typescale-body-small text-muted mb-0 mt-2 pb-2">
            {data.limitations.map(l => <li key={l}>{l}</li>)}
          </ul>
        </>
      ) : null}
    </Card>
  )
}
